/**
 * ChatBackendContext - React context for chat backend injection.
 * 
 * This module provides the context and provider for injecting chat
 * backend implementations into the React component tree, following
 * the Dependency Injection pattern.
 * 
 * @module context/ChatBackendContext
 */

import { createContext, useContext, useMemo, useEffect, type ReactNode, type ReactElement } from 'react';
import type { IChatBackend } from '../ports/IChatBackend';
import type { BackendConfig } from '../ports/types';
import { createChatBackend, type BackendType } from '../adapters/createBackend';
import { log } from '../utils/logger';

/**
 * Context value type
 */
interface ChatBackendContextValue {
  /** The current chat backend instance */
  backend: IChatBackend;
  /** The backend type identifier */
  backendType: string;
}

/**
 * The React context for chat backend
 */
const ChatBackendContext = createContext<ChatBackendContextValue | null>(null);

/**
 * Props for ChatBackendProvider when providing an existing backend instance
 */
interface ChatBackendProviderPropsWithBackend {
  /** Pre-created backend instance */
  backend: IChatBackend;
  /** Child components */
  children: ReactNode;
}

/**
 * Props for ChatBackendProvider when creating a backend from config
 */
interface ChatBackendProviderPropsWithConfig {
  /** Backend type to create */
  backendType: BackendType;
  /** Backend configuration */
  config: BackendConfig;
  /** Child components */
  children: ReactNode;
}

/**
 * Combined props type
 */
type ChatBackendProviderProps = ChatBackendProviderPropsWithBackend | ChatBackendProviderPropsWithConfig;

/**
 * Type guard to check if props contain a backend instance
 */
function hasBackendInstance(props: ChatBackendProviderProps): props is ChatBackendProviderPropsWithBackend {
  return 'backend' in props && props.backend !== undefined;
}

/**
 * ChatBackendProvider - Provides chat backend to the component tree.
 * 
 * This provider can be used in two ways:
 * 1. With a pre-created backend instance
 * 2. With backend type and configuration (creates the backend internally)
 * 
 * @example
 * ```tsx
 * // Option 1: Provide pre-created backend
 * const backend = createChatBackend('parlant', { serverUrl: '...' });
 * 
 * <ChatBackendProvider backend={backend}>
 *   <ChatInterface />
 * </ChatBackendProvider>
 * 
 * // Option 2: Create backend from config
 * <ChatBackendProvider 
 *   backendType="parlant" 
 *   config={{ serverUrl: '...' }}
 * >
 *   <ChatInterface />
 * </ChatBackendProvider>
 * ```
 */
export function ChatBackendProvider(props: ChatBackendProviderProps): ReactElement {
  const { children } = props;

  // Extract dependencies for proper memoization
  const isProvidedBackend = hasBackendInstance(props);
  const providedBackend = isProvidedBackend ? props.backend : null;
  const configBackendType = isProvidedBackend ? null : props.backendType;
  const configBackendConfig = isProvidedBackend ? null : props.config;

  // Create or use the backend
  const contextValue = useMemo<ChatBackendContextValue>(() => {
    if (providedBackend) {
      log('ChatBackendProvider: Using provided backend instance');
      return {
        backend: providedBackend,
        backendType: providedBackend.type,
      };
    } else if (configBackendType && configBackendConfig) {
      log('ChatBackendProvider: Creating backend from config:', configBackendType);
      const backend = createChatBackend(configBackendType, configBackendConfig);
      return {
        backend,
        backendType: configBackendType,
      };
    }
    throw new Error('ChatBackendProvider requires either a backend or backendType + config');
  }, [providedBackend, configBackendType, configBackendConfig]);

  // Cleanup on unmount (only if we created the backend)
  useEffect(() => {
    const backend = contextValue.backend;
    const wasCreatedInternally = !providedBackend;
    
    return () => {
      if (wasCreatedInternally && backend.dispose) {
        log('ChatBackendProvider: Disposing backend');
        backend.dispose();
      }
    };
  }, [contextValue.backend, providedBackend]);

  return (
    <ChatBackendContext.Provider value={contextValue}>
      {children}
    </ChatBackendContext.Provider>
  );
}

/**
 * Hook to access the chat backend from context.
 * 
 * This hook provides access to the chat backend instance.
 * It must be used within a ChatBackendProvider.
 * 
 * @returns The chat backend instance
 * @throws Error if used outside of ChatBackendProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const backend = useChatBackend();
 *   
 *   const handleSend = async () => {
 *     await backend.sendMessage(sessionId, 'Hello!');
 *   };
 *   
 *   return <button onClick={handleSend}>Send</button>;
 * }
 * ```
 */
export function useChatBackend(): IChatBackend {
  const context = useContext(ChatBackendContext);
  
  if (!context) {
    throw new Error(
      'useChatBackend must be used within a ChatBackendProvider. ' +
      'Wrap your component tree with <ChatBackendProvider backend={...}> or ' +
      '<ChatBackendProvider backendType="..." config={...}>.'
    );
  }
  
  return context.backend;
}

/**
 * Hook to access the chat backend context value including backend type.
 * 
 * Use this when you need to know the backend type in addition to the instance.
 * 
 * @returns Object containing backend instance and type
 * @throws Error if used outside of ChatBackendProvider
 * 
 * @example
 * ```tsx
 * function DebugInfo() {
 *   const { backend, backendType } = useChatBackendContext();
 *   return <div>Backend: {backendType}</div>;
 * }
 * ```
 */
export function useChatBackendContext(): ChatBackendContextValue {
  const context = useContext(ChatBackendContext);
  
  if (!context) {
    throw new Error(
      'useChatBackendContext must be used within a ChatBackendProvider.'
    );
  }
  
  return context;
}

/**
 * Hook to safely access the chat backend if available.
 * 
 * Unlike useChatBackend, this hook returns null if no provider is found,
 * instead of throwing an error. Useful for optional integrations.
 * 
 * @returns The chat backend instance or null
 * 
 * @example
 * ```tsx
 * function OptionalChatFeature() {
 *   const backend = useChatBackendOptional();
 *   
 *   if (!backend) {
 *     return <div>Chat not available</div>;
 *   }
 *   
 *   return <ChatWidget backend={backend} />;
 * }
 * ```
 */
export function useChatBackendOptional(): IChatBackend | null {
  const context = useContext(ChatBackendContext);
  return context?.backend ?? null;
}

/**
 * Display name for React DevTools
 */
ChatBackendContext.displayName = 'ChatBackendContext';

